import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Chip, ChipBackgroundColor } from "@amzn/stencil-react-components/chip";
import {
  Expander,
  ExpanderChevronPosition,
  ExpanderTitleRendererParams,
} from "@amzn/stencil-react-components/expander";
import { Col, GridContainer, GridItem, Hr, Row, Spacer, View } from "@amzn/stencil-react-components/layout";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { ModalContent, RenderModalFunctionParams, WithModal } from "@amzn/stencil-react-components/modal";
import { Text } from "@amzn/stencil-react-components/text";
import { IconGlobe, IconPadlock, IconPeople } from "@amzn/stencil-react-icons";
import ListModalButton, { LIST_MODAL_TYPE } from "common/components/ListModal/ListModalButton";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { getJobFamilyFromRule } from "common/helpers/lists-helper";
import { getString } from "common/uistringlabels/uiStringUtils";
import { useLists } from "hooks/ListsContext";
import { ListModel, Visibility } from "lib/models/list-models";
import { UserPermissions } from "lib/services";
import { ItemModel } from "lib/services/catalogical-models";
import React, { FC } from "react";

import { ListItem } from "./ListItem";

const normalizeListItem = (item: ItemModel): ListItem => ({
  name: item.name || item.title,
  description: item.shortDescription || item.description,
  logoUrl: item.logoUrl,
  taxonomyId: item.taxonomyId,
});

interface ListProps extends ListModel {
  isAdmin?: boolean;
}

export const List: FC<ListProps> = ({
  name,
  description,
  visibility,
  allowedDomains,
  itemCount,
  fullItems,
  listId,
  evaluationRules,
  ownerAlias,
  isAdmin,
}) => {
  const { deleteList } = useLists();
  const { addMessageBanner } = useMessageBannersBox();
  const jobFamily = getJobFamilyFromRule(evaluationRules);

  const handleConfirmDelete = async (close: () => void) => {
    try {
      await deleteList(listId);
      addMessageBanner(getString("lists.deleteSuccess"), MessageBannerType.Success);
      close();
    } catch (error) {
      addMessageBanner(getString("lists.deleteError"), MessageBannerType.Error);
      close();
    }
  };

  const renderDeleteModal = ({ close }: RenderModalFunctionParams) => (
    <ModalContent
      titleText={getString("lists.deleteConfirmation.title")}
      buttons={[
        <Button key="cancel" variant={ButtonVariant.Secondary} onClick={close}>
          {getString("lists.deleteConfirmation.cancel")}
        </Button>,
        <Button key="confirm" variant={ButtonVariant.Primary} onClick={() => handleConfirmDelete(close)}>
          {getString("lists.deleteConfirmation.confirm")}
        </Button>,
      ]}
    >
      <Text>{getString("lists.deleteConfirmation.message", { name })}</Text>
      {isAdmin && (
        <>
          <Spacer height="S200" />
          <Text>{getString("lists.deleteConfirmation.adminMessage", { jobFamily })}</Text>
        </>
      )}
    </ModalContent>
  );

  const renderVisibility = () => {
    switch (visibility) {
      case Visibility.PRIVATE:
        return (
          <Chip icon={<IconPadlock />} backgroundColor={ChipBackgroundColor.Purple} data-testid="list-visibility">
            {getString("lists.visibility.private")}
          </Chip>
        );
      case Visibility.PUBLIC:
        return (
          <Chip icon={<IconPeople />} backgroundColor={ChipBackgroundColor.Blue} data-testid="list-visibility">
            {getString("lists.visibility.public")}
          </Chip>
        );
      case Visibility.SHARED:
        return (
          <Chip icon={<IconGlobe />} backgroundColor={ChipBackgroundColor.Green} data-testid="list-visibility">
            {getString("lists.visibility.shared")}
          </Chip>
        );
      default:
        return visibility;
    }
  };

  const renderTitle = ({ titleText, toggle }: ExpanderTitleRendererParams) => {
    return (
      <Row width="100%" justifyContent="space-between">
        <Col onClick={toggle} data-testid="list-expander-trigger">
          <Text fontSize="T300" fontWeight="bold" color="neutral90" data-testid="list-name">
            {titleText}
          </Text>

          <Text fontSize="T200" color="neutral70" data-testid="list-description">
            {description}
          </Text>
        </Col>
        <Row alignItems="center" gridGap="S300">
          <Chip data-testid="list-item-count">{getString("lists.itemCount", { itemCount })}</Chip>
          {!isAdmin && renderVisibility()}
          {isAdmin && jobFamily && <Chip backgroundColor={ChipBackgroundColor.Blue}>{jobFamily}</Chip>}
          <Chip backgroundColor={ChipBackgroundColor.Yellow}>{getString("lists.owner", { ownerAlias })}</Chip>
          {!isAdmin && (
            <WithModal renderModal={renderDeleteModal}>
              {({ open }) => (
                <Button variant={ButtonVariant.Secondary} onClick={open} data-testid="list-delete-button">
                  {getString("lists.deleteList")}
                </Button>
              )}
            </WithModal>
          )}
          <ListModalButton
            initialData={{ name, description, visibility, allowedDomains, items: fullItems, evaluationRules }}
            disable={ownerAlias !== UserPermissions.current.username}
            listId={listId}
            type={isAdmin ? LIST_MODAL_TYPE.ADMIN_EDIT : LIST_MODAL_TYPE.EDIT}
            variant={ButtonVariant.Primary}
          />
        </Row>
      </Row>
    );
  };

  return (
    <View data-testid="list-container">
      <Expander
        renderTitle={renderTitle}
        titleText={name}
        chevronPosition={ExpanderChevronPosition.Leading}
        data-testid="list-expander"
      >
        <GridContainer data-testid="list-items-grid">
          {fullItems ? (
            fullItems.map((item, index) => (
              <GridItem lg={4} md={4} key={`${item.taxonomyId}-${index}`}>
                <ListItem listId={listId} item={normalizeListItem(item)} />
              </GridItem>
            ))
          ) : (
            <GridItem lg={4} md={4}>
              <Text>No Items</Text>
            </GridItem>
          )}
        </GridContainer>
      </Expander>
      <Spacer height="S300" />
      <Hr />
    </View>
  );
};
